/**
 * Compacts Depop attributes API payloads before upload. Backend only reads this
 * shape (plus the bundled fallback); it does not compact.
 */
function pickNameI18n(nameI18n: unknown): string {
  if (typeof nameI18n === "string") return nameI18n;
  if (!nameI18n || typeof nameI18n !== "object") return "";
  const record = nameI18n as Record<string, string>;
  return record["en-US"] || record.en || "";
}

function isActiveAttribute(entry: { status?: string } | null | undefined) {
  const status = entry?.status;
  return status == null || status === "active";
}

export type CompactedDepopAttributes = {
  department: Array<{ id: string; name: string }>;
  group: Array<{
    id: string;
    name: string;
    department: string[];
    product_types: Array<{
      id: string;
      name: string;
      department: string[];
    }>;
  }>;
};

export function compactDepopAttributes(
  raw: any = {}
): CompactedDepopAttributes {
  const department: CompactedDepopAttributes["department"] = [];
  for (const entry of raw.department || []) {
    if (!isActiveAttribute(entry) || !entry?.id) continue;
    department.push({
      id: entry.id,
      name: pickNameI18n(entry.name_i18n),
    });
  }

  const group: CompactedDepopAttributes["group"] = [];
  for (const entry of raw.group || []) {
    if (!isActiveAttribute(entry) || !entry?.id) continue;

    const product_types: CompactedDepopAttributes["group"][0]["product_types"] =
      [];
    for (const productType of entry.product_types || []) {
      if (!isActiveAttribute(productType) || !productType?.id) continue;
      product_types.push({
        id: productType.id,
        name: pickNameI18n(productType.name_i18n),
        department: Array.isArray(productType.department)
          ? [...productType.department]
          : [],
      });
    }

    group.push({
      id: entry.id,
      name: pickNameI18n(entry.name_i18n),
      department: Array.isArray(entry.department) ? [...entry.department] : [],
      product_types,
    });
  }

  return { department, group };
}

export const DepopAttributesMaxAgeMs = 7 * 24 * 60 * 60 * 1000;

export function depopAttributesStoragePath(uid: string) {
  return `${uid}/depop/attributes.json`;
}

export function isDepopAttributesFresh(
  payload: { updatedAt?: string } | null | undefined,
  now = Date.now()
) {
  if (!payload?.updatedAt) return false;
  const updatedAt = new Date(payload.updatedAt).getTime();
  if (!Number.isFinite(updatedAt)) return false;
  return now - updatedAt < DepopAttributesMaxAgeMs;
}
